import type {Option} from '../Option';

import Radio from './Radio';
import {CSSStyles} from '../../../../types/CSSStyles';
import Flex from '../../../layout/flex/Flex';
import useAppTheme from '../../hooks/useAppTheme';
import FormElement from '../FormElement';

import {css} from '@emotion/react';
import React from 'react';
import {useRadioGroup} from 'react-aria';
import {RadioGroupProps, useRadioGroupState} from 'react-stately';

export type OnChangeHandler<T> = (name: string, newValue: T | null) => unknown;

export type RadioGroupLayout = 'vertical' | 'horizontal';

export interface Props extends RadioGroupProps {
  readonly options: ReadonlyArray<Option>;
  readonly 'aria-label'?: string;
  readonly errorMessage?: React.ReactNode;
  readonly id?: string;
  readonly layout?: RadioGroupLayout;
  readonly sx?: CSSStyles;
}

export default function RadioGroup(props: Props): JSX.Element {
  const {layout = 'vertical', options, sx, ...radioGroupProps} = props;
  const {description, errorMessage, isRequired, label} = radioGroupProps;
  const {spacing} = useAppTheme();
  const state = useRadioGroupState(radioGroupProps);
  const {
    descriptionProps,
    errorMessageProps,
    labelProps,
    radioGroupProps: rootProps,
  } = useRadioGroup(radioGroupProps, state);

  const elements = options.map((option, i) => {
    let elementSX: CSSStyles | null = null;
    switch (layout) {
      case 'horizontal':
        elementSX = css({
          flex: '1 1 auto',
        });
        break;
      case 'vertical':
        elementSX = css({
          '&+&': {
            marginTop: spacing.x8,
          },
        });
    }
    return (
      <Radio
        ariaLabel={option.ariaLabel ?? ''}
        id={option.id}
        key={i}
        label={option.label}
        rootCSS={elementSX}
        state={state}
        value={option.value}
      />
    );
  });

  return (
    <FormElement
      description={description}
      descriptionProps={descriptionProps}
      errorMessage={errorMessage}
      errorMessageProps={errorMessageProps}
      isDisabled={state.isDisabled}
      isInvalid={state.isInvalid}
      isRequired={isRequired}
      label={label}
      labelProps={labelProps}
      rootProps={rootProps}
      sx={[sx]}
    >
      <Flex
        direction={layout === 'vertical' ? 'column' : 'row'}
        sx={css({
          rowGap: spacing.x12,
          alignItems: 'flex-start',
        })}
      >
        {elements}
      </Flex>
    </FormElement>
  );
}
